'use client'

import { useState, useTransition, useRef, useEffect, useId } from 'react'
import { toast, toastError } from '@/lib/toast'
import { Send, Loader2 } from '@/components/icons'
import {
  addFeedbackComment,
  type AddCommentResult,
  type TimelineComment,
} from '@/app/actions/feedback-user'
import { MarkdownInput } from '@/components/markdown-input'
import { isFormSubmitShortcut } from '@/lib/keyboard-utils'
import { registerPendingWorkProvider } from '@/lib/safe-refresh'
import { FEEDBACK_TRIAGE_NOTE_MAX_LENGTH } from '@/lib/feedback/limits'

interface FeedbackCommentFormProps {
  feedbackId: string
  onOptimisticAdd?: (comment: { message: string }) => void
  onOptimisticRemove?: (message: string) => void
  /** Called with the created comment on success (Issue #1742) */
  onSuccess?: (comment: TimelineComment) => void
  /**
   * The pin popover's variant (#13788): one line that grows with what is
   * typed, rather than a three-row box.
   *
   * The detail page has a whole column to spend; the popover floats over
   * somebody else's page and has to fit a status chip, a summary, a timeline
   * and a triage row in the same panel. A permanently three-row reply box
   * spent a third of a 256px-capped panel on an empty field, which is a large
   * part of why the thread scrolled almost immediately.
   *
   * The LABEL is not deleted in this variant, only made `sr-only`. The field
   * is a `contenteditable` under the hood, so the accessible name is the one
   * thing standing between a screen-reader user and an unnamed text region.
   */
  compact?: boolean
  /**
   * The action that saves the comment. Defaults to the org action; the
   * platform Feedback page (#16445) passes its own, which writes in the
   * feedback's own org.
   */
  submitAction?: (data: {
    feedbackId: string
    message: string
  }) => Promise<AddCommentResult>
}

// Aliased, not re-declared: `FEEDBACK_TRIAGE_NOTE_MAX_LENGTH` is the cap
// the triage note in `feedback-pin-popover.tsx` enforces, and the two used to
// be independent literals that documented each other (#13578).
const MAX_COMMENT_LENGTH = FEEDBACK_TRIAGE_NOTE_MAX_LENGTH

export function FeedbackCommentForm({
  feedbackId,
  onOptimisticAdd,
  onOptimisticRemove,
  onSuccess,
  compact = false,
  submitAction,
}: FeedbackCommentFormProps) {
  const [message, setMessage] = useState('')
  const [isPending, startTransition] = useTransition()
  const hasMessageRef = useRef(false)

  // Empty deps: hasMessageRef is a stable ref; provider reads .current at call time.
  useEffect(() => {
    return registerPendingWorkProvider(() => hasMessageRef.current)
  }, [])

  const isOverLimit = message.length > MAX_COMMENT_LENGTH
  const isEmpty = message.trim().length === 0

  const handleSubmit = () => {
    if (isEmpty || isOverLimit || isPending) return

    const trimmedMessage = message.trim()

    // Optimistically add the comment to the UI immediately
    if (onOptimisticAdd) {
      onOptimisticAdd({ message: trimmedMessage })
    }

    // Clear the form immediately for better UX
    setMessage('')
    hasMessageRef.current = false

    startTransition(async () => {
      // Resolved at submit, not render: the default is a server action.
      const result = await (submitAction ?? addFeedbackComment)({
        feedbackId,
        message: trimmedMessage,
      })

      if (result.success) {
        toast.success(result.message)
        // Persist the comment in parent state (Issue #1742)
        onSuccess?.(result.comment)
      } else {
        toastError({
          message: result.error,
          context: { action: 'submitFeedbackComment', feedbackId },
        })
        // Remove the optimistic comment since it failed to save
        if (onOptimisticRemove) {
          onOptimisticRemove(trimmedMessage)
        }
      }
    })
  }

  /*
   * Per-instance, never the literal `"comment"` this used to be (#13788).
   *
   * Pins are ambient now, so this form can be on screen TWICE: once as the
   * page's own comment box on `/feedback/[id]` or `/admin/feedback/[id]`, and
   * once inside a pin popover floating over it. Two nodes sharing `id="comment"`
   * make `htmlFor` resolve to the first in document order — the page's field —
   * so the popover's `sr-only` label would name the wrong control and leave its
   * own field labelled only by `ariaLabel`. axe reports `duplicate-id-active`
   * and `form-field-multiple-labels` for the same markup.
   */
  const fieldId = useId()

  return (
    <div>
      <label
        htmlFor={fieldId}
        className={
          compact
            ? 'sr-only'
            : 'block text-sm font-medium text-text-primary mb-2'
        }
      >
        Add a comment
      </label>
      {/* Issue #3326 - MarkdownInput with inline formatting */}
      <div
        className="relative"
        onKeyDown={e => {
          if (isFormSubmitShortcut(e)) {
            e.preventDefault()
            handleSubmit()
          }
        }}
      >
        <MarkdownInput
          id={fieldId}
          ariaLabel="Add a comment"
          value={message}
          onChange={val => {
            setMessage(val)
            hasMessageRef.current = val.trim().length > 0
          }}
          placeholder="Type your comment here..."
          minRows={compact ? 1 : 3}
          maxRows={compact ? 8 : 10}
          maxLength={MAX_COMMENT_LENGTH}
          disabled={isPending}
        />
        <div className="flex items-center justify-end mt-2">
          <div className="flex items-center gap-2">
            {!compact && (
              <span className="text-xs text-text-secondary hidden sm:inline">
                Press Cmd+Enter to submit
              </span>
            )}
            <button
              onClick={handleSubmit}
              disabled={isEmpty || isOverLimit || isPending}
              className="inline-flex items-center gap-2 px-4 py-2 bg-action-primary hover:bg-action-primary-hover disabled:bg-status-blue-bg-strong hover:bg-action-primary dark:disabled:bg-status-blue-bg-strong/50 text-white-static text-sm font-medium rounded-lg transition-colors disabled:cursor-not-allowed"
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Submit
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
